import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, PaperPlaneTilt, Sparkle, ArrowClockwise, Warning } from "@phosphor-icons/react";

const TONES = ["professional", "friendly", "conversational", "direct", "warm", "short"];

export default function DraftMessageModal({ open, onClose, contactId, defaultChannel = "email" }) {
  const [channel, setChannel] = useState(defaultChannel);
  const [tone, setTone] = useState("professional");
  const [reason, setReason] = useState("");
  const [desired, setDesired] = useState("");
  const [custom, setCustom] = useState("");
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState(null);

  if (!open) return null;

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/ai/contacts/${contactId}/draft`, {
        channel, tone, reason, desired_action: desired, custom_instructions: custom,
        include_subject: channel === "email",
      });
      setDraft(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setGenerating(false);
    }
  };

  const sendNow = async () => {
    if (!draft?.output?.body) return;
    setSending(true);
    try {
      if (channel === "email") {
        await api.post(`/comms/email/send`, {
          contact_id: contactId,
          subject: draft.output.subject,
          body: draft.output.body,
        });
      } else if (channel === "sms") {
        await api.post(`/sms/send`, {
          contact_id: contactId,
          body: draft.output.body,
        });
      }
      toast.success(`${channel.toUpperCase()} sent`);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSending(false);
    }
  };

  const copyToClipboard = async () => {
    const text = draft?.output?.subject
      ? `Subject: ${draft.output.subject}\n\n${draft.output.body}`
      : draft?.output?.body || "";
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch { /* noop */ }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4 z-50"
      style={{ background: "rgba(2,12,15,0.78)" }}
      onClick={onClose}
      data-testid="draft-message-modal"
    >
      <div
        className="border p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{ borderColor: "var(--border)", background: "var(--surface)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="overline flex items-center gap-2">
            <Sparkle weight="bold" style={{ color: "var(--accent)" }} /> // draft a message
          </div>
          <button onClick={onClose} className="btn-secondary text-xs px-2" data-testid="close-draft-modal"><X weight="bold" /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <div className="overline mb-1">channel</div>
            <div className="flex gap-1">
              {["email", "sms", "linkedin"].map((c) => (
                <button
                  key={c}
                  onClick={() => setChannel(c)}
                  className="tag"
                  style={{
                    color: channel === c ? "var(--bg)" : "var(--text-2)",
                    background: channel === c ? "var(--accent)" : "transparent",
                    borderColor: channel === c ? "var(--accent)" : "var(--border)",
                  }}
                  data-testid={`channel-${c}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="overline mb-1">tone</div>
            <select
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              className="w-full px-2 py-1 border bg-transparent"
              style={{ borderColor: "var(--border)" }}
              data-testid="tone-select"
            >
              {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <div className="mb-3">
          <div className="overline mb-1">reason for contact (optional)</div>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-2 py-1 border bg-transparent"
            style={{ borderColor: "var(--border)" }}
            placeholder="e.g., follow up on referral partnership chat"
            data-testid="reason-input"
          />
        </div>

        <div className="mb-3">
          <div className="overline mb-1">desired action (optional)</div>
          <input
            value={desired}
            onChange={(e) => setDesired(e.target.value)}
            className="w-full px-2 py-1 border bg-transparent"
            style={{ borderColor: "var(--border)" }}
            placeholder="e.g., schedule a 15-min call"
            data-testid="desired-action-input"
          />
        </div>

        <div className="mb-3">
          <div className="overline mb-1">custom instructions (optional)</div>
          <textarea
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            rows={2}
            className="w-full px-2 py-1 border bg-transparent"
            style={{ borderColor: "var(--border)" }}
            placeholder="e.g., keep it under 3 sentences, no exclamation marks"
            data-testid="custom-instructions-input"
          />
        </div>

        {!draft ? (
          <button
            onClick={generate}
            disabled={generating}
            className="btn-primary w-full flex items-center justify-center gap-2"
            data-testid="generate-draft-btn"
          >
            {generating ? <ArrowClockwise weight="bold" className="animate-spin" /> : <Sparkle weight="bold" />}
            {generating ? "Generating…" : "Generate draft"}
          </button>
        ) : (
          <>
            {draft.output?.subject && (
              <div className="mb-3">
                <div className="overline mb-1">subject</div>
                <input
                  value={draft.output.subject}
                  onChange={(e) => setDraft({ ...draft, output: { ...draft.output, subject: e.target.value } })}
                  className="w-full px-2 py-1 border bg-transparent"
                  style={{ borderColor: "var(--border)" }}
                  data-testid="draft-subject"
                />
              </div>
            )}
            <div className="mb-3">
              <div className="overline mb-1">body</div>
              <textarea
                value={draft.output.body}
                onChange={(e) => setDraft({ ...draft, output: { ...draft.output, body: e.target.value } })}
                rows={10}
                className="w-full px-2 py-1 border bg-transparent"
                style={{ borderColor: "var(--border)" }}
                data-testid="draft-body"
              />
            </div>
            {(draft.output.warnings || []).length > 0 && (
              <div className="mb-3 text-xs flex items-start gap-2" style={{ color: "#FF9500" }} data-testid="draft-warnings">
                <Warning weight="bold" className="mt-0.5" />
                <div>
                  <div className="font-semibold">AI omitted the following to avoid fabrication:</div>
                  <ul className="list-disc pl-4 mt-1">
                    {draft.output.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <button onClick={sendNow} disabled={sending} className="btn-primary flex items-center gap-2" data-testid="send-draft-btn">
                <PaperPlaneTilt weight="bold" /> Send {channel}
              </button>
              <button onClick={copyToClipboard} className="btn-secondary" data-testid="copy-draft-btn">Copy</button>
              <button onClick={generate} disabled={generating} className="btn-secondary flex items-center gap-2" data-testid="regenerate-draft-btn">
                <ArrowClockwise weight="bold" /> Regenerate
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
